/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import { SvgPlayFilled } from '@coursera/coursera-ui/svg';

import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import Overlay from 'bundles/xddp/components/shared/Overlay';

import _t from 'i18n!nls/career-academy';

type Props = {
  videoEmbedUrl?: string | null;
  imageUrl?: string | null;
  authorName?: string | null;
  authorTitle?: string | null;
  index: number;
};

const styles = {
  videoPlaceholderContainer: (imageUrl: string) => css`
    position: relative;
    cursor: pointer;
    z-index: 3;
    height: 375px;
    width: 100%;
    border-radius: 6px;
    box-shadow: var(--cds-color-grey-300) 0 3px 30px;
    background-image: url(${imageUrl});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    ${breakpoints.down('sm')} {
      box-shadow: var(--cds-color-grey-200) 0 3px 15px;
    }
  `,
  playIcon: css`
    border: 3px solid var(--cds-color-white-0);
    border-radius: 36px;
    width: 66px;
    height: 66px;
    padding: 14px 14px;
    margin: 0 auto;
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    :hover,
    :active,
    :focus {
      background-color: initial;
    }
  `,
  authorDetails: css`
    position: absolute;
    z-index: 2;
    bottom: 25px;
    left: 35px;
  `,
  videoOverlay: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: var(--cds-color-neutral-primary);
    opacity: 0.2;
    border-radius: 6px;
  `,
  // Default dimensions to render Youtube video properly
  videoPlayerContainer: css`
    @media (min-width: 288px) {
      height: 128px;
    }

    @media (min-width: 576px) {
      height: 324px;
    }

    @media (min-width: 792px) {
      height: 445px;
    }

    @media (min-width: 1008px) {
      height: 567px;
    }

    @media (min-width: 1152px) {
      height: 648px;
    }
  `,
};

const TestimonialVideo: React.FC<Props> = ({ videoEmbedUrl, imageUrl, authorName, authorTitle, index }) => {
  const [showVideoPlayback, setVideoPlayback] = useState(false);

  if (!videoEmbedUrl) {
    return null;
  }

  return (
    <TrackedDiv trackingName="testimonial" data={{ indexPosition: index + 1 }} withVisibilityTracking>
      <div css={styles.videoPlaceholderContainer(imageUrl ?? '')}>
        <Button
          variant="ghost"
          component={TrackedButton}
          trackingName="testimonial_video_play"
          aria-label={_t('Play now - Video presentation, #{authorName}', { authorName })}
          onClick={() => setVideoPlayback(!showVideoPlayback)}
          withVisibilityTracking={false}
          requireFullyVisible={false}
          css={styles.playIcon}
          tabIndex={-1}
        >
          <SvgPlayFilled size={36} color="white" aria-hidden="true" />
        </Button>
        <div css={styles.authorDetails}>
          <Typography2 variant="subtitleMedium" component="p" color="invertBody">
            {authorName}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary" color="invertBody">
            {authorTitle}
          </Typography2>
        </div>
        <div css={styles.videoOverlay} />
      </div>
      {showVideoPlayback && (
        <Overlay handleClose={() => setVideoPlayback(!showVideoPlayback)} isOpen={showVideoPlayback}>
          <div css={styles.videoPlayerContainer}>
            <iframe
              title={_t('Youtube Embedded Video')}
              width="100%"
              height="100%"
              src={videoEmbedUrl}
              frameBorder="0"
              allowFullScreen
            />
          </div>
        </Overlay>
      )}
    </TrackedDiv>
  );
};

export default TestimonialVideo;
